import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import loadable from '@loadable/component';

import { loadableDelay } from '@common/react/loadable/loadableSettings';
import { parseQuery } from '@common/typescript/utils/url';
import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import {
	defaultTransformFiltersBeforeHandleUrl,
	ItemsProvider,
	useItemsProviderContext,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import Button from '@common/react/components/Forms/Button';

import { capitalizeFirstLetter } from '@app/components/Utils';
import { Faq } from '@app/objects/Faq';
import { SimpleBreadCrumbs } from '@app/components/UI/SimpleBreadcrumbs/SimpleBreadcrumbs';
import ServerPageProvider from '@app/components/UI/SeverPageProviderWithLang/ServerpageProviderWithLang';
import ShareLinks from '@app/components/UI/ShareLinks/ShareLinks';
import CollapseService from '@app/components/Pages/Faq/CollapseService';

import '@app/scss/pages/faqPage.scss';

const Pagination = loadable(
	() =>
		loadableDelay(import(/* webpackChunkName: "Pagination" */ '@app/components/UI/CustomPagination')),
	{
		fallback: (
			<div className="text-center">
				<Button className="btn-sm btn btn-primary" isLoading>
					Loading
				</Button>
			</div>),
	},
);

interface FaqListPageInnerProps {
	title: string;
}

const FaqListPageInner: React.FC<FaqListPageInnerProps> = ({
	title,
}) => {
	const context = useItemsProviderContext<Faq>();

	if (!context.state) throw 'Need ItemsProvider context!';

	const { state: { items, filters, pagination }, actions: { handleChange, reload } } = context;

	const { t, i18n } = useTranslation();
	const langName: 'Es' | 'En' = capitalizeFirstLetter(i18n.language);

	return <>
		<SimpleSearchInput
			name="text"
			containerClassName="dark-search"
			placeholder={t('forms.search')}
			onSubmit={handleChange}
			defaultValue={filters.text}
			withoutForm
		/>
		{items
			&& <div className="service-page__block">
				<CollapseService items={items} langName={langName} search={filters.text} hideEmpty={false} />
				{pagination.total > 10
					&& <Pagination
						showSizeChanger
						className="dark-pagination"
						pageSize={pagination.pageSize}
						pageSizeOptions={[10, 20, 30, 40]}
						defaultCurrent={pagination.current}
						total={pagination.total ? pagination.total : 0}
						onChange={(page, pageSize) => reload({ current: page, pageSize })}
					/>
				}
			</div>
		}
	</>;
};

const FaqListPage: React.FC = () => {
	const { t } = useTranslation();

	const location = useLocation();

	const search = parseQuery(location.search);
	const defCurrent = +search.page || 1;
	const title = t('site.Faqs');

	return (
		<div className="faq-page inner-page container">
			<div className="clearfix">
				<SimpleBreadCrumbs title={title} />
				<ShareLinks />
			</div>
			<div className="inner-page__headline text-center">
				<h1>{t('site.Faqs')}</h1>
			</div>
			<ServerPageProvider
				path="faq"
				inner={(serverPage) =>
					<ItemsProvider
						items={serverPage?.items?.list}
						pagination={{
							current: defCurrent, pageSize: 10, total: serverPage?.items?.count,
						}}
						type="faq"
						loadRequest="faqViewListRemote"
						filters={{ page: defCurrent, withRows: true }}
						withHandleUrl
						pageInSearch
						transformFiltersBeforeHandleUrl={(filters) => ({
							...defaultTransformFiltersBeforeHandleUrl(filters),
							page: filters.page || 1,
						})}
					>
						<FaqListPageInner
							title={title}
						/>
					</ItemsProvider>
				}
			/>
		</div>
	);
};

export default FaqListPage;
